<!--
Key Dates screen
@author rb304x
-->

<template>
  <div id="contain" class="container">
    <Nav name="Key Dates" :step="true"/>
    <Spinner ref="spinner" />
      <div class="eventNavContainer">
        <ul class="eventNavUl nav nav-tabs">
          <li @click="filterKeyDates('day')" class="eventNav" :class="{ dateActiveN: selected[0] }">
            Today
          </li>
          <li @click="filterKeyDates('week')" class="eventNav" :class="{ dateActiveN: selected[1] }">
            Next 7 days
          </li>
          <li @click="filterKeyDates('beyond')" class="eventNav" :class="{ dateActiveN: selected[2] }">
            Beyond
          </li>
        </ul>
      </div>
      <div class="eventsFilter" >
        <select class="filterSelect" @change="addCategoryFilter()" v-model="filterCategory" aria-label="Date category">
          <option value=-1>All Categories</option>
          <option v-bind:key="c.id" :value="c.id" v-for="c in categories" >{{ c.name }}</option>
        </select>
      </div>
      <div v-if="results.length == 0" class="eventsNone">
        No key dates <span v-if="filterCategory != -1">(key dates are also currently filtered by category above)</span>
      </div>
      <div v-if="results.length > 0">
        <KeyDate v-for="(keyDate) in results" :key="keyDate.id" :ref="'keyDate' + keyDate.id"
          :title="keyDate.title" :startDate="keyDate.startDate"
          :endDate="keyDate.endDate" :keyDate="keyDate" />
      </div>

    <div id="pusher"></div>
    <Footer active="keyDates"/>
  </div>
</template>

<script setup>
//Utilities
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'

//Composables
import {useStringUtil} from '@/composables/StringMethods.js'
const { isEmpty } = useStringUtil()

//Components
import KeyDate from '@/components/dates/KeyDate.vue'
import Footer from '@/components/FooterMenu.vue'

//Data
const results = ref({})
const categories = ref([])
const selected = ref([true, false, false])
const filterCategory = ref(-1)
const keyDates = ref({})

//Component Refs
const spinner = ref(null)

const view = computed(() => {
  if (selected.value[0] === true) {
    return 'day'
  }
  if (selected.value[1] === true) {
    return 'week'
  }
  return 'beyond'
})

onMounted(() => {
  getCategories()
  getKeyDates()
})

function getCategories () {
  axios.get('/timetable/keydates/categories/', { withCredentials: true })
    .then(response => {
      categories.value = response.data
    })
}

function getKeyDates () {
  spinner.value.spin()
  axios.get('/timetable/keydates/upcoming', { withCredentials: true })
    .then(response => {
      keyDates.value = response.data
      spinner.value.stop()
      filterKeyDates('day')
    })
}

function nullProtect (tme) {
  return isEmpty(tme) ? '00:00' : tme
}

function sortKeyDates () {
  // const a = this
  if (results.value) {
    results.value.sort(function (x, y) {
      let e1 = new Date(x.startDate)
      let e2 = new Date(y.startDate)
      if (e1.startDate < e2.startDate) return -1
      if (e1.startDate > e2.startDate) return 1
      // Put same day keyDates in alphabetical order
      if (e1.title > e2.title) return 1
      if (e1.title < e2.title) return -1
      return 0
    })
  }
}

function addCategoryFilter () {
  // this is another filter - filterKeyDates does the job so changing the dropdown will just trigger it
  filterKeyDates(view.value)
}

function filterKeyDates (type) {
  const dateNow = new Date()
  const dayFromNow = new Date()
  const weekFromNow = new Date()
  dateNow.setHours(0, 0, 0, 0)
  dayFromNow.setHours(0, 0, 0, 0)
  weekFromNow.setHours(0, 0, 0, 0)
  dayFromNow.setDate(dateNow.getDate() + 1)
  weekFromNow.setDate(dateNow.getDate() + 7)
  results.value = keyDates.value

  // Work out where to display each keydate

  if (results.value) {
    results.value.map(e => {
      const evSDate = new Date(e.startDate)
      const evEDate = new Date(e.endDate)
      // console.log('comparing dateNow: ' + dateNow + ' and evSDate: ' + evSDate + ' and dayFromNow: ' + dayFromNow)
      if (dateNow <= evSDate && evSDate < dayFromNow) {
        e.dispTime = 'day'
      } else if (dayFromNow <= evSDate && evSDate < weekFromNow) {
        e.dispTime = 'week'
      } else if (weekFromNow <= evSDate) {
        e.dispTime = 'beyond'
      } else { // it's in the past. base display on the end date as per WCSC-1497
        if (evEDate >= dateNow) {
          e.dispTime = 'day'
        } else {
          e.dispTime = 'PAST'
        }
      }
    })
  }

  selected.value = [type === 'day', type === 'week', type === 'beyond']
  if (results.value) {
    results.value = results.value.filter(e => e.dispTime == type)
  }

  if (filterCategory.value != -1) {
    // console.log('filtering on category id ' + this.filterCategory + ' ...')
    results.value = results.value.filter(e => e.category.id == filterCategory.value)
  }
  sortKeyDates()
}
  

</script>

<style scoped lang="scss">
.eventsFilter {
  width: 100%;
  padding: 20px 25px;
  position: relative;
}

.eventsFilter:after {
  display: block;
  content: "";
  background-image: url('@/assets/chevron-down-light.svg');
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  height: 24px;
  width: 24px;
  top: 50%;
  right: 35px;
  -webkit-transform: translateY(-50%);
  transform: translateY(-50%);
  z-index:-1;
}
.filterSelect {
  width: 100%;
  border-radius: 8px;
  border: 2px solid rgba(0, 56, 101, .25);
  background: transparent;
  height: 44px;
  padding: 10px 50px 10px 18px;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: left;
  -moz-appearance: none;
  /* for Chrome */
  -webkit-appearance: none;
  line-height: 1rem;
  color: var(--uog-dark-blue);
}

.filterSelect::-ms-expand {
    display: none;
}

.filterSelect:hover {
  cursor:pointer;
}
.eventsNone {
  margin-top:15px;
  text-align: center;
  color: #aaa;
}
.eventNavContainer {
  overflow:hidden;
  display: inline;
  white-space:nowrap;
}

.eventNav {
    width: 33.3333% !important;
    /* below inherits from another .eventNav */
    /*text-align: center;
    line-height: 50px;
    height: 50px;*/
}

.eventNavUl::-webkit-scrollbar {
  display: none;
}

.dateActiveN {
  border-bottom: 2px solid var(--uog-dark-blue);
}

.eventNavUl {
  background-color: var(--uog-blue-10);
}

li.eventNav {
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align:center;
  color: var(--uog-dark-blue);
  height: 44px;
}

.notifActionText {
  float:left;
  margin-left:16px;
  margin-top:32px;
  color:#4F5961;
}

</style>
